import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaMobileAlt, FaWallet, FaRupeeSign, FaSatelliteDish, FaBolt, FaWater, FaGasPump, FaHome, FaCreditCard } from 'react-icons/fa';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';

// PaySprint operator IDs from getoperator API
const operatorMap = {
  Airtel: 11,      // PaySprint ID from getoperator API
  Vodafone: 22,    // PaySprint ID from getoperator API
  Jio: 18,         // PaySprint ID from getoperator API
  BSNL: 13,        // PaySprint ID from getoperator API
  Idea: 4,         // PaySprint ID from getoperator API
  'Vi': 22,        // Vodafone-Idea merged (same as Vodafone)
};

const dthOperators = ['Tata Sky', 'Airtel Digital TV', 'Dish TV', 'Videocon D2H', 'Sun Direct', 'Reliance Digital TV'];

const circleOptions = [
  'Delhi NCR',
  'Mumbai',
  'Maharashtra Goa',
  'Andhra Pradesh Telangana',
  'Assam',
  'Bihar Jharkhand',
  'Chennai',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jammu Kashmir',
  'Karnataka',
  'Kerala',
  'Kolkata',
  'Madhya Pradesh Chhattisgarh',
  'North East',
  'Orissa',
  'Punjab',
  'Rajasthan',
  'Tamil Nadu',
  'UP East',
  'UP West',
  'West Bengal',
];

// Utility bill services
const utilityServices = [
  { id: 'electricity', name: 'Electricity', icon: FaBolt, fieldLabel: 'Consumer Number', placeholder: 'Enter consumer number' },
  { id: 'gas', name: 'Gas', icon: FaGasPump, fieldLabel: 'Consumer Number', placeholder: 'Enter consumer number' },
  { id: 'water', name: 'Water', icon: FaWater, fieldLabel: 'Consumer Number', placeholder: 'Enter consumer number' },
  { id: 'landline', name: 'Landline', icon: FaHome, fieldLabel: 'Landline Number', placeholder: 'Enter landline number' },
  { id: 'broadband', name: 'Broadband', icon: FaCreditCard, fieldLabel: 'Account Number', placeholder: 'Enter account number' },
  { id: 'insurance', name: 'Insurance', icon: FaCreditCard, fieldLabel: 'Policy Number', placeholder: 'Enter policy number' },
];

const RechargeBillPage = () => {
  const navigate = useNavigate();
  const [mainCategory, setMainCategory] = useState('recharge'); // 'recharge' or 'utilities'
  const [activeTab, setActiveTab] = useState('prepaid'); // For recharge: 'prepaid', 'postpaid', 'dth'
  const [selectedUtility, setSelectedUtility] = useState(utilityServices[0].id); // For utilities
  const [formData, setFormData] = useState({
    mobile: '',
    operator: '',
    circle: circleOptions[0],
    amount: '',
    operator_id: '', // For utilities
    ad1: '',
    ad2: '',
    ad3: '',
  });
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingBill, setFetchingBill] = useState(false);
  const [billFetchData, setBillFetchData] = useState(null);
  const [billInfo, setBillInfo] = useState(null);
  const [dthInfo, setDthInfo] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [operators, setOperators] = useState([]); // All operators from PaySprint
  const [filteredOperators, setFilteredOperators] = useState([]); // Filtered by selected utility
  const [loadingOperators, setLoadingOperators] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState(null); // Selected operator object with viewbill, regex, etc.

  useEffect(() => {
    fetchWalletBalance();
  }, []);

  /**
   * Check if operator is unavailable
   * Filters out operators that should not be shown
   */
  const isUnavailable = useCallback((op) => {
    if (!op) return true;
    
    // Check available flag (from whitelist)
    if (op.available === false) return true;
    
    // Check name/displayname for "temporarily unavailable" text
    const name = String(op.name || '').toLowerCase();
    const displayname = String(op.displayname || '').toLowerCase();
    
    if (name.includes('temporarily unavailable')) return true;
    if (displayname.includes('temporarily unavailable')) return true;
    
    // Check status field if exists
    if (op.status && String(op.status).toLowerCase() !== 'online') {
      // Only filter if explicitly marked as offline/unavailable
      if (String(op.status).toLowerCase() === 'offline' || 
          String(op.status).toLowerCase() === 'unavailable') {
        return true;
      }
    }
    
    return false;
  }, []);

  const filterOperatorsByCategory = useCallback((ops) => {
    if (!ops || ops.length === 0) {
      setFilteredOperators([]);
      console.log('🔍 No operators to filter');
      return;
    }

    // PaySprint API returns operators based on mode parameter
    // For utilities, mode defaults to 'offline' which returns all BBPS offline operators
    // Filter by category/name/displayname matching selected utility
    const categoryMap = {
      'electricity': ['electricity', 'power', 'energy', 'electric', 'bijli', 'vidyut', 'discom'],
      'gas': ['gas', 'lpg', 'cng', 'cylinder'],
      'water': ['water', 'jal', 'water supply'],
      'landline': ['landline', 'telephone', 'bsnl', 'mtnl'],
      'broadband': ['broadband', 'internet', 'fiber', 'wifi'],
      'insurance': ['insurance', 'lic', 'life insurance', 'health insurance', 'policy']
    };

    const categories = categoryMap[selectedUtility] || [];
    
    // Step 1: Filter by category
    let filtered = ops;
    if (categories.length > 0) {
      const categoryFiltered = ops.filter(op => {
        const opCategory = String(op.category || '').toLowerCase();
        const opName = String(op.name || '').toLowerCase();
        const opDisplayName = String(op.displayname || '').toLowerCase();
        
        // Check if any category keyword matches in any field
        const matches = categories.some(cat => {
          const catLower = cat.toLowerCase();
          return opCategory.includes(catLower) || 
                 opName.includes(catLower) ||
                 opDisplayName.includes(catLower);
        });
        
        return matches;
      });
      
      if (categoryFiltered.length > 0) {
        filtered = categoryFiltered;
      }
    }
    
    // Step 2: Filter out unavailable operators (completely remove, don't just disable)
    const availableOperators = filtered.filter(op => !isUnavailable(op));
    
    // Step 3: Sort operators - Delhi providers first, then alphabetically
    const delhiOperatorIds = [11, 12, 20]; // BSES Rajdhani, BSES Yamuna, Tata Power Delhi
    const sortedOperators = availableOperators.sort((a, b) => {
      const aIsDelhi = delhiOperatorIds.includes(Number(a.id));
      const bIsDelhi = delhiOperatorIds.includes(Number(b.id));
      
      // Delhi providers first
      if (aIsDelhi && !bIsDelhi) return -1;
      if (!aIsDelhi && bIsDelhi) return 1;
      
      // Within same group, sort alphabetically by name
      const nameA = String(a.name || '').toLowerCase();
      const nameB = String(b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    
    console.log('🔍 Operator Filtering Complete:', {
      selectedUtility,
      total_operators: ops.length,
      after_category_filter: filtered.length,
      after_availability_filter: availableOperators.length,
      final_count: sortedOperators.length,
      delhi_providers: sortedOperators.filter(op => delhiOperatorIds.includes(Number(op.id))).map(op => op.name)
    });

    setFilteredOperators(sortedOperators);
    
    // Clear operator_id if current selection is not in the list
    if (formData.operator_id && !sortedOperators.find(op => String(op.id) === String(formData.operator_id))) {
      console.log(`🗑️ Previously selected operator_id ${formData.operator_id} not found in filtered list. Resetting.`);
      setFormData(prev => ({ ...prev, operator_id: '', ad1: '', ad2: '', ad3: '' }));
      setSelectedOperator(null);
    }
  }, [selectedUtility, formData.operator_id, isUnavailable]);

  const fetchOperators = useCallback(async () => {
    setLoadingOperators(true);
    setError('');
    try {
      // For utilities (electricity, gas, etc.), use 'offline' mode (MANDATORY)
      // For postpaid mobile, use 'online' mode
      // Mode is now mandatory - always pass it explicitly
      const response = await api.get('/api/bill-payment/operators', {
        params: {
          mode: 'offline' // Mandatory: offline for utilities
        }
      });

      console.log('🔍 Bill Payment Operators API Response:', {
        success: response.data.success,
        hasData: !!response.data.data,
        hasDataData: !!response.data.data?.data,
        dataStructure: response.data.data,
        operatorsCount: response.data.data?.data?.length || 0,
        firstOperator: response.data.data?.data?.[0] || null
      });

      if (response.data.success && response.data.data?.data) {
        const ops = response.data.data.data;
        console.log('✅ Operators received:', {
          total: ops.length,
          sample: ops.slice(0, 3),
          allOperatorIds: ops.map(op => ({ id: op.id, name: op.name || op.displayname, category: op.category }))
        });
        setOperators(ops);
        // Filter will be applied by the useEffect hook
      } else {
        console.error('❌ Failed to load operators:', {
          success: response.data.success,
          message: response.data.message,
          data: response.data.data
        });
        setError('Failed to load operators. Please try again.');
        setOperators([]);
        setFilteredOperators([]);
      }
    } catch (err) {
      console.error('Failed to fetch operators', err);
      setError('Failed to load operators. Please try again.');
      setOperators([]);
      setFilteredOperators([]);
    } finally {
      setLoadingOperators(false);
    }
  }, []);

  // Fetch operators when utilities tab is selected
  useEffect(() => {
    if (mainCategory === 'utilities') {
      fetchOperators();
    }
  }, [mainCategory, fetchOperators]);

  // Filter operators when utility service changes
  useEffect(() => {
    if (mainCategory === 'utilities' && operators.length > 0) {
      filterOperatorsByCategory(operators);
    }
  }, [selectedUtility, operators, mainCategory, filterOperatorsByCategory]);

  // ✅ Optional UX fix: Clear billFetchData when operator changes
  useEffect(() => {
    if (mainCategory === 'utilities') {
      setBillFetchData(null);
      setBillInfo(null);
    }
  }, [formData.operator_id, mainCategory]);

  const fetchWalletBalance = async () => {
    try {
      const response = await api.get('/api/wallet/balance');
      if (response.data.success) {
        setBalance(response.data.balance);
      }
    } catch (err) {
      console.error('Failed to fetch wallet balance', err);
    }
  };

  const handleMainCategoryChange = (category) => {
    setMainCategory(category);
    if (category === 'recharge') {
      setActiveTab('prepaid');
    } else {
      setSelectedUtility(utilityServices[0].id);
    }
    setFormData({
      mobile: '',
      operator: '',
      circle: circleOptions[0],
      amount: '',
      operator_id: '',
    });
    setBillFetchData(null);
    setBillInfo(null);
    setDthInfo(null);
    setError('');
    setSuccessMessage('');
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFormData({
      mobile: '',
      operator: '',
      circle: circleOptions[0],
      amount: '',
      operator_id: '',
    });
    setBillFetchData(null);
    setBillInfo(null);
    setDthInfo(null);
    setError('');
    setSuccessMessage('');
  };

  const handleUtilityChange = (utilityId) => {
    setSelectedUtility(utilityId);
    setFormData({
      mobile: '',
      operator: '',
      circle: circleOptions[0],
      amount: '',
      operator_id: '',
    });
    setBillFetchData(null);
    setBillInfo(null);
    setError('');
    setSuccessMessage('');
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Validate canumber against operator regex
  const validateCanumber = (canumber, operator) => {
    if (!operator || !operator.regex) return true; // No regex = valid
    
    try {
      const regex = new RegExp(operator.regex);
      return regex.test(canumber);
    } catch (e) {
      console.error('Invalid regex pattern:', operator.regex);
      return true; // If regex is invalid, allow it
    }
  };

  // Validate ad fields against operator regex
  const validateAdField = (value, regex) => {
    if (!regex) return true;
    try {
      const regexPattern = new RegExp(regex);
      return regexPattern.test(value);
    } catch (e) {
      return true;
    }
  };

  const handleFetchBill = async () => {
    if (mainCategory === 'utilities') {
      // ✅ NEW: If viewbill = 0, fetch is NOT required (direct pay only)
      if (selectedOperator && String(selectedOperator.viewbill) === "0") {
        setError('This operator does not require bill fetch. You can pay directly.');
        toast.info('No bill fetch required. Proceed to Pay Bill.');
        return;
      }

      if (!formData.mobile || formData.mobile.trim() === '') {
        setError('Enter consumer number before fetching the bill.');
        return;
      }
      if (!formData.operator_id || !selectedOperator) {
        setError('Select provider/operator before fetching the bill.');
        return;
      }

      // Validate canumber against operator regex
      if (!validateCanumber(formData.mobile, selectedOperator)) {
        setError(`Invalid ${selectedOperator.displayname || 'consumer number'} format. Please check and try again.`);
        return;
      }

      // ✅ FIX: ad fields are REQUIRED if operator specifies ad*_name
      if (selectedOperator.ad1_name) {
        if (!formData.ad1 || String(formData.ad1).trim() === '') {
          setError(`Enter ${selectedOperator.ad1_name}.`);
          return;
        }
        if (!validateAdField(formData.ad1, selectedOperator.ad1_regex)) {
          setError(`Invalid ${selectedOperator.ad1_name} format.`);
          return;
        }
      }
      if (selectedOperator.ad2_name) {
        if (!formData.ad2 || String(formData.ad2).trim() === '') {
          setError(`Enter ${selectedOperator.ad2_name}.`);
          return;
        }
        if (!validateAdField(formData.ad2, selectedOperator.ad2_regex)) {
          setError(`Invalid ${selectedOperator.ad2_name} format.`);
          return;
        }
      }
      if (selectedOperator.ad3_name) {
        if (!formData.ad3 || String(formData.ad3).trim() === '') {
          setError(`Enter ${selectedOperator.ad3_name}.`);
          return;
        }
        if (!validateAdField(formData.ad3, selectedOperator.ad3_regex)) {
          setError(`Invalid ${selectedOperator.ad3_name} format.`);
          return;
        }
      }
    } else {
      if (!formData.mobile || formData.mobile.length !== 10) {
        setError('Enter a valid 10 digit mobile number before fetching the bill.');
        return;
      }
      if (!formData.operator) {
        setError('Select operator before fetching the bill.');
        return;
      }
      if (!getBillOperatorId()) {
        setError('Selected operator is not supported yet.');
        return;
      }
    }

    setFetchingBill(true);
    setError('');
    try {
      const isPostpaid = mainCategory === 'recharge' && activeTab === 'postpaid';

      // Get operator ID
      let operatorId = mainCategory === 'utilities' ? selectedOperator?.id : getBillOperatorId();
      operatorId = Number(operatorId);

      // Log operator selection before fetching bill
      console.log('🔵 Fetching Bill - Operator Details:', {
        operator_id: operatorId,
        operator_name: selectedOperator?.name || 'N/A',
        operator_display_name: selectedOperator?.displayname || 'N/A',
        category: mainCategory,
        utility: selectedUtility,
        consumer_number: formData.mobile,
        is_postpaid: isPostpaid,
        selected_operator_object: selectedOperator
      });

      // Validate postpaid operator IDs
      if (isPostpaid) {
        const validPostpaidIds = [11, 22, 18, 13]; // Airtel, Vodafone, Jio, BSNL
        if (!validPostpaidIds.includes(operatorId)) {
          setError(`Invalid operator ID: ${operatorId}. Please select a valid postpaid operator (Airtel, Vodafone, Jio, or BSNL).`);
          setFetchingBill(false);
          return;
        }
      }

      const payload = {
        // ✅ utilities: operator from selectedOperator.id, recharge/postpaid: from getBillOperatorId()
        operator: operatorId,
        canumber: String(formData.mobile || '').trim(),
        // For postpaid bill fetch, explicitly mark as postpaid so backend uses mode=online
        ...(isPostpaid ? { payment_type: 'postpaid' } : {}),
      };

      // Add ad1/ad2/ad3 only if they exist and have values
      if (selectedOperator?.ad1_name) payload.ad1 = String(formData.ad1 || '').trim();
      if (selectedOperator?.ad2_name) payload.ad2 = String(formData.ad2 || '').trim();
      if (selectedOperator?.ad3_name) payload.ad3 = String(formData.ad3 || '').trim();

      const response = await api.post('/api/bill-payment/fetch-bill', payload);

      if (response.data.success) {
        const data = response.data.data?.data || response.data.data;
        setBillFetchData(data);
        setBillInfo(data);
        if (data.amount || data.billAmount) {
          setFormData((prev) => ({ ...prev, amount: data.amount || data.billAmount || '' }));
        }
        toast.success('Bill details fetched');
      } else {
        // ✅ FIX: Show user-friendly error message
        const errorMessage = response.data.message || 'Unable to fetch bill details';
        setError(errorMessage);
        
        // Show toast for service down errors
        if (response.data.error_code === 'SERVICE_DOWN' || response.data.response_code === 4) {
          toast.error('Bill payment service is temporarily unavailable. Please try again later.');
        } else {
          toast.error(errorMessage);
        }
      }
    } catch (err) {
      console.error('Fetch bill error', err);
      
      // ✅ FIX: Handle different HTTP status codes
      const status = err.response?.status;
      const errorData = err.response?.data;
      
      let errorMessage = 'Unable to fetch bill details';
      
      if (status === 503 || errorData?.error_code === 'SERVICE_DOWN' || errorData?.response_code === 4) {
        errorMessage = 'Bill payment service is temporarily down. Please try again later.';
        toast.error(errorMessage);
      } else if (errorData?.message) {
        errorMessage = errorData.message;
        toast.error(errorMessage);
      } else {
        toast.error(errorMessage);
      }
      
      setError(errorMessage);
    } finally {
      setFetchingBill(false);
    }
  };

  const getBillOperatorId = () => operatorMap[formData.operator] || formData.operator;
  const getRechargeOperatorValue = () => formData.operator;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');

    // Validation based on category
    if (mainCategory === 'utilities') {
      // Utilities validation
      if (!formData.mobile || formData.mobile.trim() === '') {
        const utility = utilityServices.find(u => u.id === selectedUtility);
        setError(`Please enter ${utility?.fieldLabel || 'consumer number'}.`);
        return;
      }
      if (!formData.operator_id) {
        setError('Please select an operator/provider.');
        return;
      }
      
      // Check if selected operator is available (whitelist check)
      if (selectedOperator && selectedOperator.available === false) {
        setError('This operator is temporarily unavailable. Please select another operator.');
        toast.error('This operator is temporarily unavailable. Please select another operator.');
        return;
      }
    } else {
      // Recharge validation
      if (activeTab === 'dth') {
        if (!formData.mobile || formData.mobile.length < 6) {
          setError('Please enter a valid customer/subscriber ID.');
          return;
        }
      } else {
        if (!formData.mobile || formData.mobile.length !== 10) {
          setError('Please enter a valid 10 digit mobile number.');
          return;
        }
      }
      if (!formData.operator) {
        setError('Please select an operator.');
        return;
      }
    }

    if (!formData.amount || Number(formData.amount) <= 0) {
      setError('Please enter a valid amount.');
      return;
    }
    if (balance !== null && Number(formData.amount) > Number(balance)) {
      setError('Insufficient wallet balance.');
      return;
    }
    if (activeTab === 'postpaid' && !billFetchData) {
      setError('Please fetch bill details before paying.');
      return;
    }

    setLoading(true);
    try {
      let endpoint;
      let payload;

      if (mainCategory === 'utilities') {
        // Utilities bill payment
        const op = selectedOperator; // from operator list
        
        // Check viewbill: if 0, direct pay; if 1, must have fetched bill
        if (String(op?.viewbill) === "1" && !billFetchData) {
          setError('Please fetch bill details before paying.');
          setLoading(false);
          return;
        }

        // ✅ NEW: Validate required ad fields BEFORE pay (important for viewbill=0 operators)
        if (op?.ad1_name) {
          if (!formData.ad1 || String(formData.ad1).trim() === '') {
            setError(`Enter ${op.ad1_name}.`);
            setLoading(false);
            return;
          }
          if (!validateAdField(formData.ad1, op.ad1_regex)) {
            setError(`Invalid ${op.ad1_name} format.`);
            setLoading(false);
            return;
          }
        }
        if (op?.ad2_name) {
          if (!formData.ad2 || String(formData.ad2).trim() === '') {
            setError(`Enter ${op.ad2_name}.`);
            setLoading(false);
            return;
          }
          if (!validateAdField(formData.ad2, op.ad2_regex)) {
            setError(`Invalid ${op.ad2_name} format.`);
            setLoading(false);
            return;
          }
        }
        if (op?.ad3_name) {
          if (!formData.ad3 || String(formData.ad3).trim() === '') {
            setError(`Enter ${op.ad3_name}.`);
            setLoading(false);
            return;
          }
          if (!validateAdField(formData.ad3, op.ad3_regex)) {
            setError(`Invalid ${op.ad3_name} format.`);
            setLoading(false);
            return;
          }
        }

        endpoint = '/api/bill-payment/pay-bill';
        payload = {
          // ✅ FIX: Always send operator from selectedOperator.id
          operator_id: Number(op?.id),
          consumer_number: String(formData.mobile || '').trim(),
          amount: Number(formData.amount),
          reference_id: uuidv4(), // Generate unique reference ID
          mode: 'offline', // Utilities use offline mode
        };

        // ✅ FIX: Include bill_fetch if bill was fetched (required for viewbill=1 operators)
        if (billFetchData) {
          payload.bill_fetch = billFetchData;
        }

        // Add ad1/ad2/ad3 only if they exist and have values
        if (op?.ad1_name) payload.ad1 = String(formData.ad1 || '').trim();
        if (op?.ad2_name) payload.ad2 = String(formData.ad2 || '').trim();
        if (op?.ad3_name) payload.ad3 = String(formData.ad3 || '').trim();
      } else if (activeTab === 'postpaid') {
        endpoint = '/api/bill-payment/pay-bill';
        payload = {
          operator_id: getBillOperatorId(),
          consumer_number: formData.mobile,
          amount: formData.amount,
          payment_type: 'postpaid',
          reference_id: uuidv4(),
          bill_fetch: billFetchData,
        };
      } else if (activeTab === 'dth') {
        endpoint = '/api/recharge/process';
        payload = {
          mobile: formData.mobile,
          operator: getRechargeOperatorValue(),
          amount: formData.amount,
          type: 'dth',
          idempotency_key: uuidv4(),
        };
      } else {
        endpoint = '/api/recharge/process';
        payload = {
          mobile: formData.mobile,
          operator: getRechargeOperatorValue(),
          amount: formData.amount,
          type: 'mobile',
          idempotency_key: uuidv4(),
        };
      }

      const response = await api.post(endpoint, payload);
      if (response.data.success) {
        let successText = 'Transaction successful!';
        if (mainCategory === 'utilities') {
          const utility = utilityServices.find(u => u.id === selectedUtility);
          successText = `${utility?.name || 'Bill'} payment successful!`;
        } else if (activeTab === 'postpaid') {
          successText = 'Bill payment successful!';
        } else if (activeTab === 'dth') {
          successText = 'DTH recharge successful!';
        } else {
          successText = 'Recharge successful!';
        }
        setSuccessMessage(successText);
        toast.success(successText);
        fetchWalletBalance();
        setBillFetchData(null);
        setBillInfo(null);
        setFormData((prev) => ({ ...prev, amount: '' }));
      } else {
        setError(response.data.message || 'Transaction failed. Please try again.');
      }
    } catch (err) {
      console.error('Transaction error', err);
      setError(err.response?.data?.message || 'Transaction failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <div className="bg-gradient-to-r from-indigo-900 via-blue-900 to-blue-700 text-white py-6 shadow">
        <div className="max-w-5xl mx-auto px-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-sm font-semibold text-blue-100 mb-4 hover:text-white transition"
            type="button"
          >
            <FaArrowLeft className="mr-2" />
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold">Recharge & Bill Payment</h1>
          <p className="text-blue-100 mt-2">
            Instant recharge of your prepaid/postpaid mobile or DTH connection with the BT India dashboard look & feel.
          </p>
        </div>
      </div>

      <div className="flex-1 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-6">
            {/* Wallet Balance */}
            <div className="mb-6">
              <p className="text-xs uppercase tracking-wide text-gray-500">Wallet Balance</p>
              <div className="flex items-center text-2xl font-semibold text-blue-700">
                <FaWallet className="mr-2" />
                {balance !== null ? `₹${Number(balance).toFixed(2)}` : '---'}
              </div>
            </div>

            {/* Main Category Tabs */}
            <div className="flex border-b border-gray-200 mb-6">
              <button
                className={`px-6 py-3 text-sm font-semibold border-b-2 transition ${
                  mainCategory === 'recharge'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => handleMainCategoryChange('recharge')}
                type="button"
              >
                <FaMobileAlt className="inline mr-2" />
                Recharge
              </button>
              <button
                className={`px-6 py-3 text-sm font-semibold border-b-2 transition ${
                  mainCategory === 'utilities'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => handleMainCategoryChange('utilities')}
                type="button"
              >
                <FaBolt className="inline mr-2" />
                Utilities & Bills
              </button>
            </div>

            {/* Sub-tabs for Recharge */}
            {mainCategory === 'recharge' && (
              <div className="flex border border-gray-200 rounded-xl overflow-hidden mb-6">
                <button
                  className={`px-6 py-2 text-sm font-semibold ${
                    activeTab === 'prepaid' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={() => handleTabChange('prepaid')}
                  type="button"
                >
                  PREPAID
                </button>
                <button
                  className={`px-6 py-2 text-sm font-semibold ${
                    activeTab === 'postpaid' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={() => handleTabChange('postpaid')}
                  type="button"
                >
                  POSTPAID
                </button>
                <button
                  className={`px-6 py-2 text-sm font-semibold ${
                    activeTab === 'dth' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={() => handleTabChange('dth')}
                  type="button"
                >
                  DTH
                </button>
              </div>
            )}

            {/* Utility Service Selection */}
            {mainCategory === 'utilities' && (
              <div className="mb-6">
                <label className="text-xs font-semibold text-gray-500 uppercase mb-2 block">Select Utility Service</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {utilityServices.map((utility) => {
                    const IconComponent = utility.icon;
                    return (
                      <button
                        key={utility.id}
                        type="button"
                        onClick={() => handleUtilityChange(utility.id)}
                        className={`p-4 border-2 rounded-lg text-center transition ${
                          selectedUtility === utility.id
                            ? 'border-blue-600 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }`}
                      >
                        <IconComponent className="mx-auto mb-2 text-xl" />
                        <div className="text-sm font-semibold">{utility.name}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">
                    {mainCategory === 'utilities'
                      ? (selectedOperator?.displayname || utilityServices.find(u => u.id === selectedUtility)?.fieldLabel || 'Consumer Number')
                      : activeTab === 'dth'
                      ? 'Customer ID'
                      : 'Mobile No'}
                    {mainCategory === 'utilities' && selectedOperator && (
                      <span className="ml-2 text-xs text-gray-400 font-normal normal-case">
                        ({selectedOperator.name})
                      </span>
                    )}
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={
                      mainCategory === 'utilities'
                        ? selectedOperator?.displayname 
                          ? `Enter ${selectedOperator.displayname}`
                          : (utilityServices.find(u => u.id === selectedUtility)?.placeholder || 'Enter consumer number')
                        : activeTab === 'dth'
                        ? 'Enter customer/subscriber ID'
                        : 'Enter 10 digit mobile number'
                    }
                    value={formData.mobile}
                    onChange={(e) =>
                      handleInputChange(
                        'mobile',
                        mainCategory === 'utilities' || activeTab === 'dth'
                          ? e.target.value.replace(/[^0-9A-Za-z]/g, '').slice(0, 20)
                          : e.target.value.replace(/[^0-9]/g, '').slice(0, 10)
                      )
                    }
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">
                    {mainCategory === 'utilities' ? 'Provider/Operator' : 'Operator'}
                    {mainCategory === 'utilities' && loadingOperators && (
                      <span className="ml-2 text-xs text-gray-400 font-normal">(Loading...)</span>
                    )}
                  </label>
                  {mainCategory === 'utilities' ? (
                    <select
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.operator_id}
                      onChange={(e) => {
                        const opId = e.target.value;
                        // Find and set selected operator
                        const op = filteredOperators.find(o => String(o.id) === String(opId));
                        
                        // Check if operator is available
                        if (op && op.available === false) {
                          toast.error('This operator is temporarily unavailable. Please select another operator.');
                          return; // Don't set unavailable operator
                        }
                        
                        handleInputChange('operator_id', opId);
                        const selectedOp = op || null;
                        setSelectedOperator(selectedOp);
                        
                        // Log operator selection for tracking
                        if (selectedOp) {
                          console.log('🔵 Operator Selected from Dropdown:', {
                            operator_id: selectedOp.id,
                            operator_name: selectedOp.name,
                            display_name: selectedOp.displayname,
                            category: selectedOp.category,
                            viewbill: selectedOp.viewbill,
                            available: selectedOp.available,
                            utility: selectedUtility
                          });
                        }
                        // Clear ad fields when operator changes
                        setFormData(prev => ({ ...prev, ad1: '', ad2: '', ad3: '' }));
                      }}
                      disabled={loadingOperators}
                    >
                      <option value="">
                        {loadingOperators 
                          ? 'Loading operators...' 
                          : filteredOperators.length === 0 
                          ? 'No operators available' 
                          : 'Select provider/operator'}
                      </option>
                      {/* Delhi Providers Group (Popular) */}
                      {filteredOperators.filter(op => [11, 12, 20].includes(Number(op.id))).length > 0 && (
                        <optgroup label="⭐ Popular in Delhi">
                          {filteredOperators
                            .filter(op => [11, 12, 20].includes(Number(op.id)))
                            .map((operator) => (
                              <option key={operator.id} value={operator.id}>
                                {operator.name || `Operator ${operator.id}`}
                              </option>
                            ))}
                        </optgroup>
                      )}
                      {/* Other Providers */}
                      {filteredOperators.filter(op => ![11, 12, 20].includes(Number(op.id))).length > 0 && (
                        <optgroup label={selectedUtility === 'electricity' ? 'Other Electricity Providers' : 'Other Providers'}>
                          {filteredOperators
                            .filter(op => ![11, 12, 20].includes(Number(op.id)))
                            .map((operator) => (
                              <option key={operator.id} value={operator.id}>
                                {operator.name || `Operator ${operator.id}`}
                              </option>
                            ))}
                        </optgroup>
                      )}
                    </select>
                  ) : (
                    <select
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.operator}
                      onChange={(e) => handleInputChange('operator', e.target.value)}
                    >
                      <option value="">Select operator</option>
                      {(activeTab === 'dth' ? dthOperators : Object.keys(operatorMap)).map(
                        (operator) => (
                          <option key={operator} value={operator}>
                            {operator}
                          </option>
                        )
                      )}
                    </select>
                  )}
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Amount</label>
                  <div className="flex items-center border border-gray-200 rounded-lg px-4 py-3 mt-1 focus-within:ring-2 focus-within:ring-blue-500">
                    <FaRupeeSign className="text-gray-400 mr-2" />
                    <input
                      type="number"
                      className="w-full focus:outline-none"
                      placeholder="0.00"
                      value={formData.amount}
                      onChange={(e) => handleInputChange('amount', e.target.value)}
                      min="10"
                    />
                  </div>
                </div>
                {mainCategory === 'recharge' && (
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase">Circle</label>
                    <select
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.circle}
                      onChange={(e) => handleInputChange('circle', e.target.value)}
                    >
                      {circleOptions.map((circle) => (
                        <option key={circle} value={circle}>
                          {circle}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {mainCategory === 'recharge' && activeTab === 'postpaid' && (
                <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <p className="text-sm text-gray-600">
                      Fetch the latest bill before paying to auto-fill the exact amount.
                    </p>
                    {billInfo && (
                      <p className="text-xs text-green-600 mt-1">
                        Bill fetched for account {billInfo?.customerName || billInfo?.name || formData.mobile}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleFetchBill}
                    className="px-5 py-2 rounded-lg border border-blue-600 text-blue-600 font-semibold hover:bg-blue-50 transition"
                  >
                    {fetchingBill ? 'Fetching...' : 'Fetch Bill Details'}
                  </button>
                </div>
              )}

              {/* Dynamic ad1/ad2/ad3 fields based on selected operator */}
              {mainCategory === 'utilities' && selectedOperator && (
                <div className="grid md:grid-cols-3 gap-4 mt-4">
                  {selectedOperator.ad1_name && (
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase">
                        {selectedOperator.ad1_name}
                      </label>
                      <input
                        type="text"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={`Enter ${selectedOperator.ad1_name}`}
                        value={formData.ad1}
                        onChange={(e) => handleInputChange('ad1', e.target.value)}
                      />
                    </div>
                  )}
                  {selectedOperator.ad2_name && (
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase">
                        {selectedOperator.ad2_name}
                      </label>
                      <input
                        type="text"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={`Enter ${selectedOperator.ad2_name}`}
                        value={formData.ad2}
                        onChange={(e) => handleInputChange('ad2', e.target.value)}
                      />
                    </div>
                  )}
                  {selectedOperator.ad3_name && (
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase">
                        {selectedOperator.ad3_name}
                      </label>
                      <input
                        type="text"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={`Enter ${selectedOperator.ad3_name}`}
                        value={formData.ad3}
                        onChange={(e) => handleInputChange('ad3', e.target.value)}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Show fetch bill button only if viewbill = 1 */}
              {mainCategory === 'utilities' && selectedOperator && String(selectedOperator.viewbill) === "1" && (
                <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <p className="text-sm text-gray-600">
                      Fetch the latest bill before paying to auto-fill the exact amount.
                    </p>
                    {billInfo && (
                      <p className="text-xs text-green-600 mt-1">
                        Bill fetched for account {billInfo?.customerName || billInfo?.name || formData.mobile}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleFetchBill}
                    className="px-5 py-2 rounded-lg border border-blue-600 text-blue-600 font-semibold hover:bg-blue-50 transition"
                  >
                    {fetchingBill ? 'Fetching...' : 'Fetch Bill Details'}
                  </button>
                </div>
              )}

              {mainCategory === 'recharge' && activeTab === 'dth' && (
                <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <p className="text-sm text-gray-600">
                      View subscriber info to auto-fill the recommended recharge amount.
                    </p>
                    {dthInfo && (
                      <p className="text-xs text-green-600 mt-1">
                        Subscriber: {dthInfo?.CustomerName || dthInfo?.name || formData.mobile} | Balance:{' '}
                        {dthInfo?.Balance || dthInfo?.MonthlyRecharge || 'N/A'}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!formData.mobile || !formData.operator) {
                        setError('Enter customer ID and select operator to fetch DTH info.');
                        return;
                      }
                      setLoading(true);
                      setError('');
                      try {
                        const response = await api.post('/api/hlr/dth-info', {
                          canumber: formData.mobile,
                          op: getRechargeOperatorValue(),
                        });
                        if (response.data.success) {
                          const data = response.data.data?.data || response.data.data;
                          setDthInfo(data);
                          if (data?.MonthlyRecharge || data?.Balance) {
                            setFormData((prev) => ({
                              ...prev,
                              amount: data.MonthlyRecharge || data.Balance || '',
                            }));
                          }
                          toast.success('DTH info fetched successfully');
                        } else {
                          setError(response.data.message || 'Unable to fetch DTH info');
                        }
                      } catch (err) {
                        console.error('DTH info error', err);
                        setError(err.response?.data?.message || 'Unable to fetch DTH info');
                      } finally {
                        setLoading(false);
                      }
                    }}
                    className="px-5 py-2 rounded-lg border border-blue-600 text-blue-600 font-semibold hover:bg-blue-50 transition"
                  >
                    <FaSatelliteDish className="inline mr-2" />
                    View DTH Info
                  </button>
                </div>
              )}

              {error && (
                <div className="mt-4 bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {successMessage && (
                <div className="mt-4 bg-green-50 text-green-700 px-4 py-3 rounded-lg text-sm">
                  {successMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg flex items-center justify-center transition"
              >
                {loading ? (
                  'Processing...'
                ) : (
                  <>
                    {mainCategory === 'utilities' ? (
                      <>
                        <FaBolt className="mr-2" />
                        Pay {utilityServices.find(u => u.id === selectedUtility)?.name || 'Bill'}
                      </>
                    ) : activeTab === 'dth' ? (
                      <>
                        <FaSatelliteDish className="mr-2" />
                        Continue To Recharge
                      </>
                    ) : (
                      <>
                        <FaMobileAlt className="mr-2" />
                        Continue To Recharge
                      </>
                    )}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RechargeBillPage;


